import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiMail, FiLock, FiLogIn } from 'react-icons/fi';
import { login, checkAuth } from '../redux/slices/authSlice';
import { fetchCampaignAnalytics } from '../redux/slices/dashboardSlice';
import { fetchUsers } from '../redux/slices/userSlice';
import toast from 'react-hot-toast';
import RippleButton from '../components/ui/ripple-button';
import { Particles } from '../components/ui/particles';

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading, error } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  // Error mesajını string'e çevir
  const getErrorMessage = (error) => {
    if (!error) return '';
    if (typeof error === 'string') return error;
    if (error.message) return error.message;
    if (error.error) return error.error;
    if (typeof error === 'object') {
      // Obje ise önemli alanları kontrol et
      if (error.success !== undefined && error.message) {
        return error.message;
      }
      return JSON.stringify(error);
    }
    return 'Bilinmeyen hata';
  };

  // Sayfa yüklendiğinde auth durumunu kontrol et
  useEffect(() => {
    dispatch(checkAuth());
  }, [dispatch]);

  // Zaten giriş yapılmışsa önceki sayfaya veya dashboard'a yönlendir
  useEffect(() => {
    if (isAuthenticated) {
      // Eğer bir sayfadan yönlendirildiyse oraya dön, yoksa dashboard'a git
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      toast.error('Lütfen e-posta ve şifre girin');
      return;
    }

    // Promise zincirinde unhandled kalmaması için unwrap kullanmadan güvenli kontrol
    const action = await dispatch(login(formData)).catch((err) => {
      // Beklenmedik bir durumda da yut
      console.error('Login dispatch hatası:', err);
      return null;
    });

    if (!action || action.meta?.requestStatus === 'rejected') {
      // Hata mesajı thunk içinde toast olarak gösteriliyor
      return;
    }

      dispatch(fetchCampaignAnalytics({ sections: 'core' }));
      dispatch(fetchUsers());

      // Önceki sayfaya veya dashboard'a yönlendir
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
      <Particles
        className="fixed inset-0 w-full h-full"
        quantity={100}
        ease={80}
        color="#1e40af"
      />
      <div className="relative z-10 max-w-md w-full">
        {/* Logo/Başlık */}
        <div className="text-center mb-4">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-500 rounded-xl mb-4 shadow-lg">
            <FiLogIn className="text-2xl text-white" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Admin Paneli</h1>
          <p className="text-gray-500">Hesabınıza giriş yapın</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-xl shadow-xl p-4">
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                E-posta
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiMail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="ornek@example.com"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Şifre
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiLock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="••••••••"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-sm text-red-800">{getErrorMessage(error)}</p>
              </div>
            )}

            {/* Submit Button */}
            <RippleButton
              type="submit"
              disabled={loading}
              className="w-full"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Giriş yapılıyor...
                </>
              ) : (
                <>
                  <FiLogIn className="h-5 w-5" />
                  Giriş Yap
                </>
              )}
            </RippleButton>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          © 2025 Yeni Koza Admin Panel. Tüm hakları saklıdır.
        </p>
      </div>
    </div>
  );
};

export default Login;

